import { createContext, useCallback, useContext, useEffect, useState, PropsWithChildren, useRef } from 'react';
import supabaseClient from '@/api/supabaseClient.ts';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import useLogin from '@/api/useLogin.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import useVerifyEmail from '@/api/useVerifyEmail.ts';
import useResendVerificationCode from '@/api/useResendVerificationCode.ts';

interface UserWithRole {
  id: string;
  role: 'resident' | 'vendor' | 'admin';
  email: string;
}

interface AuthContextProps {
  session: Session | null;
  user: UserWithRole | null;
  supabaseClient: SupabaseClient;
  logout: () => void;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  const logout = useCallback(async () => {
    const { error } = await supabaseClient.auth.signOut();
    if (error) {
      console.error('Logout failed:', error.message);
    } else {
      setUser(null);
      setSession(null);
    }
  }, []);

  const loginQuery = useLogin();

  const sendResetPasswordLinkQuery = useSendResetPasswordLink();

  const resetPasswordQuery = useResetPassword();

  const verifyEmailQuery = useVerifyEmail();

  const resendVerificationCodeQuery = useResendVerificationCode();

  const checkData = useCallback(async (session: Session | null, forceRefresh = false) => {
    if (isPendingAuthChangeCheck.current && !forceRefresh) return;

    isPendingAuthChangeCheck.current = true;

    try {
      if (!session) {
        setSession(null);
        setUser(null);
        setIsLoading(false);
        return;
      }
      const { data: profile, error } = await supabaseClient
        .from('profiles')
        .select('id, role, email')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }

        setUser(null);
        setSession(session);
        setIsLoading(false);
        return;
      }

      if (profile) {
        setUser({
          id: profile.id,
          role: profile.role,
          email: profile.email || session.user.email || '',
        });
        setSession(session);
      } else {
        setUser(null);
        setSession(session);
      }
    } catch (err) {
      console.error('Unexpected error in checkData:', err);
      setUser(null);
      setSession(null);
    } finally {
      setIsLoading(false);
      isPendingAuthChangeCheck.current = false;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await supabaseClient.auth.getSession();
    if (data.session) {
      await checkData(data.session, true);
    }
  }, [checkData]);

  useEffect(() => {
    console.log('AuthProvider mounted');

    const initializeAuth = async () => {
      console.log('Initializing auth...');
      const { data } = await supabaseClient.auth.getSession();
      console.log('Initial session:', data.session);
      await checkData(data.session);
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', _event, session);
      checkData(session);
    });

    return () => {
      console.log('AuthProvider unmounting');
      subscription.unsubscribe();
      setSession(null);
    };
  }, [checkData]);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        supabaseClient,
        logout,
        refreshUser,
        isLoading,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
